import {ModelDTO} from 'hemwb-api';
import {ModelFilterState} from './types';

export const extractDistinctMetadata = (models: ModelDTO[] | null, field: string) =>
  Object.values(
    (models || [])
      .map((model) => (model.metadata ? model.metadata[field] : null))
      .filter((metadata): metadata is string => !!metadata)
      .reduce((map, metadata) => {
        (metadata?.split('|') || []).forEach((value) => {
          if (field === 'platform' && value === '') {
            value = 'Model Repository';
          }
          map[value] = value;
        });
        return map;
      }, {} as any as {[key: string]: string}),
  ).sort();

const matchesFilter = (model: ModelDTO, filter: ModelFilterState): boolean => {
  // Status filter - use default if not specified
  const statusFilter = filter.status?.length ? filter.status : ['Draft', 'Published'];
  if (!statusFilter.includes(model.metadata.status)) {
    return false;
  }

  // Model types filter
  if (filter.modelTypes?.length && !filter.modelTypes.includes(model.metadata.model_type)) {
    return false;
  }

  // Global/Country adaptation filter
  if (filter.global?.length) {
    const isGlobal = model.metadata.global === 'true';
    const isCountryAdaptation = !!model.metadata.country;

    const matchesGlobal = filter.global.includes('global') && isGlobal;
    const matchesCountry = filter.global.includes('countryAdaptation') && isCountryAdaptation;

    if (!matchesGlobal && !matchesCountry) {
      return false;
    }
  }

  // Countries filter
  if (filter.countries?.length && !filter.countries.includes(model.metadata.country)) {
    return false;
  }

  // Stewards filter
  if (filter.stewards?.length) {
    if (
      !filter.stewards.some((steward) => {
        // Handle both object format and string format "Name (email)"
        if (typeof steward === 'string') {
          const match = steward.match(/^(.+)\s\((.+)\)$/);
          if (match) {
            const [, name, email] = match;
            return model.metadata.name_of_model_steward === name && model.metadata.email_for_model_steward === email;
          }
          return false;
        }
        return (
          model.metadata.name_of_model_steward === steward.name &&
          model.metadata.email_for_model_steward === steward.email
        );
      })
    ) {
      return false;
    }
  }

  // Creators filter
  if (filter.creators?.length && !filter.creators.some((creator) => model.createdBy.id === creator.id)) {
    return false;
  }

  // Products filter
  if (filter.products?.length && !filter.products.includes(model.metadata.product)) {
    return false;
  }

  // Diseases filter
  if (filter.diseases?.length && !filter.diseases.includes(model.metadata.disease)) {
    return false;
  }

  // Type filter (model language)
  if (filter.type?.length && !filter.type.includes(model.metadata.type)) {
    return false;
  }

  // Therapeutic areas filter
  if (filter.therapeuticAreas?.length && !filter.therapeuticAreas.includes(model.metadata.therapeutic_area)) {
    return false;
  }

  // Platforms filter
  if (filter.platforms?.length && !filter.platforms.includes(model.metadata.platform)) {
    return false;
  }

  return true;
};

export const filterModels = (models: ModelDTO[], filter: ModelFilterState): ModelDTO[] => {
  return models
    .map((model) => {
      // Filter the parent model
      if (!matchesFilter(model, filter)) {
        return null;
      }

      // If model has children, filter them too
      if (model.children && model.children.length > 0) {
        const filteredChildren = model.children.filter((child) => matchesFilter(child, filter));

        // Return parent with filtered children
        return {
          ...model,
          children: filteredChildren,
        };
      }

      // Return parent without children
      return model;
    })
    .filter((model): model is ModelDTO => model !== null);
};

export const mapOptions = (options: string[]): {label: string; value: string}[] => {
  return options.map((option) => ({label: option, value: option}));
};
